import {IPaginatedReportData, emptyPaginatedReportData} from "../../entities/reports/IReportData";
import {FetchState} from "../../entities/FetchState";
import { Reducer } from "redux";
import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";
import Either from "../../utils/Either";
import {callApiWithResult, ICallResult, IError} from "../../utils/callApi";


/**********************************************************
 * API Data
 **********************************************************/
export interface IData {
    streamId: string
    uuid: string
    fullName: string
    lastName: string
    firstName: string
    burgerServiceNumber: string | null
    birthYear: number | null
    email: string | null
    homePhone: string | null
    mobilePhone: string | null
    residencyCityPostalCode: string | null
    residencyCityName: string | null
}
export const defaultClientList: IPaginatedReportData<IData> = emptyPaginatedReportData()

/**********************************************************
 * State
 **********************************************************/

export interface IState {
    ClientList: IPaginatedReportData<IData>
    SearchTerm: string | null
}
export const InitialState: IState = {
    ClientList: defaultClientList,
    SearchTerm: null
}

/**********************************************************
 * Action Types
 **********************************************************/
export enum ActionTypes {
    FetchData = "client.ClientList Fetch",
    FetchDataSuccess = "client.ClientList FetchSuccess",
    FetchDataFailed = "client.ClientList FetchFailed",
    LoadMore = "client.ClientList LoadMore",
    LoadMoreSuccess = "client.ClientList LoadMoreSuccess",
    LoadMoreFailed = "client.ClientList LoadMoreFailed",
    SetSearchTerm = "client.ClientList SetSearchTerm",
    Reset = "client.ClientList Reset"
}

/**********************************************************
 * Actions & Creators
 **********************************************************/
export interface IFetchData {
    type: ActionTypes.FetchData,
    payload?: { search?: string }
}
export interface IFetchDataSuccess {
    type: ActionTypes.FetchDataSuccess,
    payload: { data: IData[], hasMore: boolean }
}
export interface IFetchDataFailed {
    type: ActionTypes.FetchDataFailed,
    payload: { errorMessage: string }
}
export interface ILoadMore {
    type: ActionTypes.LoadMore
}
export interface ILoadMoreSuccess {
    type: ActionTypes.LoadMoreSuccess,
    payload: { data: IData[], hasMore: boolean }
}
export interface ILoadMoreFailed {
    type: ActionTypes.LoadMoreFailed,
    payload: { errorMessage: string }
}
export interface ISetSearchTerm {
    type: ActionTypes.SetSearchTerm,
    payload: { searchTerm: string | null }
}
export interface IReset {
    type: ActionTypes.Reset
}
type Actions = IFetchData | IFetchDataSuccess | IFetchDataFailed | ILoadMore | ILoadMoreSuccess | ILoadMoreFailed | ISetSearchTerm | IReset;

export class ActionCreators {
    public static FetchData(search?: string): IFetchData {
        return { 
            type: ActionTypes.FetchData,
            payload: { search }
        };
    }
    public static FetchDataSuccess(newData: IData[], hasMore: boolean): IFetchDataSuccess {
        return {
            type: ActionTypes.FetchDataSuccess,
            payload: { data: newData, hasMore }
        };
    }
    public static FetchDataFailed(err: string): IFetchDataFailed {
        return {
            type: ActionTypes.FetchDataFailed,
            payload: { errorMessage: err }
        };
    }
    public static LoadMore(): ILoadMore {
        return {
            type: ActionTypes.LoadMore
        };
    }
    public static LoadMoreSuccess(newData: IData[], hasMore: boolean): ILoadMoreSuccess {
        return {
            type: ActionTypes.LoadMoreSuccess,
            payload: { data: newData, hasMore }
        };
    }
    public static LoadMoreFailed(err: string): ILoadMoreFailed {
        return {
            type: ActionTypes.LoadMoreFailed,
            payload: { errorMessage: err }
        };
    }
    public static SetSearchTerm(searchTerm: string | null): ISetSearchTerm {
        return {
            type: ActionTypes.SetSearchTerm,
            payload: { searchTerm }
        };
    }
    public static Reset(): IReset {
        return {
            type: ActionTypes.Reset
        };
    }
}

/**********************************************************
 * Reducer
 **********************************************************/
export const Reducers: Reducer<IState, Actions> = (state = InitialState, action) => {
    switch (action.type) {
        case ActionTypes.FetchData:
            return { 
                ...state, 
                ClientList: {
                    ...state.ClientList,
                    state: FetchState.Busy,
                    currentPage: 1,
                    data: [] // Clear data when fetching fresh
                }
            };
        case ActionTypes.FetchDataSuccess:
            return { 
                ...state, 
                ClientList: {
                    ...state.ClientList,
                    state: FetchState.Success,
                    data: action.payload.data,
                    hasMore: action.payload.hasMore,
                    currentPage: 1,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.FetchDataFailed:
            return { 
                ...state, 
                ClientList: {
                    ...state.ClientList,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.LoadMore:
            return {
                ...state,
                ClientList: {
                    ...state.ClientList,
                    loadingMoreState: FetchState.Busy
                }
            };
        case ActionTypes.LoadMoreSuccess:
            return {
                ...state,
                ClientList: {
                    ...state.ClientList,
                    data: [...state.ClientList.data, ...action.payload.data], // Append new data
                    hasMore: action.payload.hasMore,
                    currentPage: state.ClientList.currentPage + 1,
                    loadingMoreState: FetchState.Success,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.LoadMoreFailed:
            return {
                ...state,
                ClientList: {
                    ...state.ClientList,
                    loadingMoreState: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.SetSearchTerm:
            return {
                ...state,
                SearchTerm: action.payload.searchTerm
                // Note: The actual fetch will be triggered by saga/component
            };
        case ActionTypes.Reset:
            return {
                ...InitialState
            };
        default:
            return state;
    }
}

/**********************************************************
 * Sagas
 **********************************************************/

function* handleIFetchData(action: IFetchData) {
    try {
        const search = action.payload?.search;
        const pageSize = defaultClientList.pageSize;
        
        let url = `/v1/Client?page=1&pageSize=${pageSize}`;
        if (search) {
            url += `&search=${encodeURIComponent(search)}`;
        }
        
        const response : Either<IError, ICallResult<IData[]>> = yield call(callApiWithResult, 'GET', url);
        yield response
            .FlatMap(success => {
                const hasMore = success.result.length >= pageSize;
                return put(ActionCreators.FetchDataSuccess(success.result, hasMore) as any);
            })
            .Reduce(error => put(ActionCreators.FetchDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.FetchDataFailed(JSON.stringify(err)))
    }
}

function* handleILoadMore(_action: ILoadMore) {
    try {
        const state: IState = yield select((s: any) => s.client.clientList);
        const { SearchTerm, ClientList } = state;
        const nextPage = ClientList.currentPage + 1;
        const pageSize = ClientList.pageSize;
        
        let url = `/v1/Client?page=${nextPage}&pageSize=${pageSize}`;
        if (SearchTerm) {
            url += `&search=${encodeURIComponent(SearchTerm)}`;
        }
        
        const response : Either<IError, ICallResult<IData[]>> = yield call(callApiWithResult, 'GET', url);
        yield response
            .FlatMap(success => {
                const hasMore = success.result.length >= pageSize;
                return put(ActionCreators.LoadMoreSuccess(success.result, hasMore) as any);
            })
            .Reduce(error => put(ActionCreators.LoadMoreFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.LoadMoreFailed(JSON.stringify(err)))
    }
}

function* watchAll() {
    yield takeEvery(ActionTypes.FetchData, handleIFetchData);
    yield takeEvery(ActionTypes.LoadMore, handleILoadMore);
}
export function* sagas() {
    yield all([fork(watchAll)]);
}


